import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, ArrowLeft, Send, Paperclip, Download, X,
    Armchair, Zap, Droplets, Sparkles,
    Package, Wind, Building, KeyRound, HelpCircle,
    CircleDot, CircleCheck, Circle, ChevronRight,
    UserCheck, MessageSquare, Search, RotateCcw,
    Clock, BarChart3, MapPin,
    Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import './TicketsSoporte.css';

// ============================================================
// CONSTANTES
// ============================================================

const CATEGORIAS = [
    { key: 'mobiliario', label: 'Mobiliario', icon: Armchair, color: '#8b5cf6' },
    { key: 'electricidad', label: 'Electricidad', icon: Zap, color: '#f59e0b' },
    { key: 'plomeria', label: 'Plomería', icon: Droplets, color: '#3b82f6' },
    { key: 'limpieza', label: 'Limpieza', icon: Sparkles, color: '#10b981' },
    { key: 'mudanza', label: 'Mudanza / Traslado', icon: Package, color: '#6366f1' },
    { key: 'aire_acondicionado', label: 'Aire Acondicionado', icon: Wind, color: '#06b6d4' },
    { key: 'infraestructura', label: 'Infraestructura', icon: Building, color: '#64748b' },
    { key: 'cerrajeria', label: 'Cerrajería', icon: KeyRound, color: '#dc2626' },
    { key: 'otro', label: 'Otro', icon: HelpCircle, color: '#94a3b8' },
];

const ESTADOS = {
    abierto: { label: 'Abierto', icon: Circle, color: '#3b82f6', bg: '#eff6ff' },
    en_progreso: { label: 'En progreso', icon: CircleDot, color: '#f59e0b', bg: '#fffbeb' },
    cerrado: { label: 'Cerrado', icon: CircleCheck, color: '#10b981', bg: '#ecfdf5' },
};

const UBICACIONES = [
    { key: 'CA1', label: 'CA1' },
    { key: 'CDN', label: 'CDN' },
];

const getCatInfo = (key) => CATEGORIAS.find(c => c.key === key) || { key, label: key, icon: HelpCircle, color: '#64748b' };

// ============================================================
// COMPONENTES AUXILIARES
// ============================================================

const StatusBadge = ({ status }) => {
    const s = ESTADOS[status] || ESTADOS.abierto;
    const Icon = s.icon;
    return (
        <span className="tk-status-badge" style={{ color: s.color, backgroundColor: s.bg }}>
            <Icon size={14} /> {s.label}
        </span>
    );
};

const CategoryBadge = ({ categoria }) => {
    const cat = getCatInfo(categoria);
    const Icon = cat.icon;
    return (
        <span className="tk-cat-badge" style={{ '--cat-color': cat.color }}>
            <Icon size={14} /> {cat.label}
        </span>
    );
};

const SlaTimer = ({ ticket }) => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        if (ticket.status === 'cerrado') return;
        const t = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(t);
    }, [ticket.status]);

    if (!ticket.created_at || !ticket.sla_hours) return null;
    const created = new Date(ticket.created_at);
    const deadlineMs = created.getTime() + ticket.sla_hours * 3600000;

    if (ticket.status === 'cerrado' && ticket.closed_at) {
        const closed = new Date(ticket.closed_at);
        const h = ((closed - created) / 3600000).toFixed(1);
        const met = parseFloat(h) <= ticket.sla_hours;
        return (
            <span className={`tk-sla ${met ? 'green' : 'red'}`}>
                <Clock size={14} /> Resuelto en {h}h {met ? '' : `(SLA: ${ticket.sla_hours}h)`}
            </span>
        );
    }

    const remainingMs = deadlineMs - now.getTime();
    if (remainingMs < 0) {
        const oh = Math.abs(remainingMs / 3600000).toFixed(1);
        return <span className="tk-sla red"><Clock size={14} /> Vencido hace {oh}h</span>;
    }
    const totalMs = ticket.sla_hours * 3600000;
    const pct = remainingMs / totalMs;
    const colorCls = pct < 0.25 ? 'red' : pct < 0.5 ? 'yellow' : 'green';
    const h = Math.floor(remainingMs / 3600000);
    const m = Math.floor((remainingMs % 3600000) / 60000);
    return <span className={`tk-sla ${colorCls}`}><Clock size={14} /> {h}h {m}m restantes</span>;
};

// ============================================================
// FUNCIONES AUXILIARES
// ============================================================

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin}m`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `Hace ${diffH}h`;
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return 'Ayer';
    if (diffD < 7) return `Hace ${diffD}d`;
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatFullDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('es-VE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

const SolicitudServicio = () => {
    const { user } = useAuth();
    const authToken = localStorage.getItem('token');
    const isAdmin = user?.role === 'generales';

    // Vistas y navegacion
    const [view, setView] = useState('list');
    const [adminTab, setAdminTab] = useState('tickets');
    const [selectedTicket, setSelectedTicket] = useState(null);

    // Lista
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCat, setFilterCat] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Crear
    const [form, setForm] = useState({ titulo: '', descripcion: '', categoria: '', ubicacion: '' });
    const [formFiles, setFormFiles] = useState([]);
    const [creating, setCreating] = useState(false);

    // Detalle
    const [ticketDetail, setTicketDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [comment, setComment] = useState('');
    const [commentFiles, setCommentFiles] = useState([]);
    const [sendingComment, setSendingComment] = useState(false);
    const sendingCommentRef = useRef(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const commentsEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const commentFileRef = useRef(null);

    // Reportes
    const [reports, setReports] = useState(null);
    const [loadingReports, setLoadingReports] = useState(false);

    // ============================================================
    // EFECTOS Y CARGA DE DATOS
    // ============================================================

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = isAdmin ? '/api/servicios/admin' : '/api/servicios';
            const res = await axiosInstance.get(endpoint);
            setTickets(res.data);
        } catch (err) { console.error('Error cargando solicitudes:', err); }
        finally { setLoading(false); }
    }, [isAdmin]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    useEffect(() => {
        if (adminTab === 'reports' && !reports) {
            setLoadingReports(true);
            axiosInstance.get('/api/servicios/reports')
                .then(r => setReports(r.data))
                .catch(() => {})
                .finally(() => setLoadingReports(false));
        }
    }, [adminTab, reports]);

    // ============================================================
    // HANDLERS
    // ============================================================

    const openTicket = async (ticketId) => {
        setView('detail');
        setLoadingDetail(true);
        try {
            const res = await axiosInstance.get(`/api/servicios/${ticketId}`);
            setTicketDetail(res.data);
            setSelectedTicket(ticketId);
        } catch (err) { console.error('Error cargando solicitud:', err); }
        finally { setLoadingDetail(false); }
    };

    const scrollToLastComment = () => {
        if (commentsEndRef.current) commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.titulo.trim() || !form.descripcion.trim() || !form.categoria) return;
        setCreating(true);
        try {
            const fd = new FormData();
            fd.append('titulo', form.titulo);
            fd.append('descripcion', form.descripcion);
            fd.append('categoria', form.categoria);
            if (form.ubicacion) fd.append('ubicacion', form.ubicacion);
            formFiles.forEach(f => fd.append('files', f));
            await axiosInstance.post('/api/servicios', fd);
            setForm({ titulo: '', descripcion: '', categoria: '', ubicacion: '' });
            setFormFiles([]);
            setView('list');
            fetchTickets();
        } catch (err) { console.error('Error creando solicitud:', err); }
        finally { setCreating(false); }
    };

    const handleComment = async () => {
        if (sendingCommentRef.current) return;
        if (!comment.trim() && commentFiles.length === 0) return;
        sendingCommentRef.current = true;
        setSendingComment(true);
        try {
            const fd = new FormData();
            fd.append('content', comment);
            commentFiles.forEach(f => fd.append('files', f));
            await axiosInstance.post(`/api/servicios/${selectedTicket}/comments`, fd);
            setComment('');
            setCommentFiles([]);
            const res = await axiosInstance.get(`/api/servicios/${selectedTicket}`);
            setTicketDetail(res.data);
            setTimeout(scrollToLastComment, 100);
        } catch (err) { console.error('Error enviando comentario:', err); }
        finally { sendingCommentRef.current = false; setSendingComment(false); }
    };

    const handleStatusChange = async (newStatus) => {
        setUpdatingStatus(true);
        try {
            await axiosInstance.put(`/api/servicios/${selectedTicket}/status`, { status: newStatus });
            const res = await axiosInstance.get(`/api/servicios/${selectedTicket}`);
            setTicketDetail(res.data);
            fetchTickets();
        } catch (err) { console.error('Error cambiando estado:', err); }
        finally { setUpdatingStatus(false); }
    };

    const handleAssign = async () => {
        try {
            await axiosInstance.put(`/api/servicios/${selectedTicket}/assign`);
            const res = await axiosInstance.get(`/api/servicios/${selectedTicket}`);
            setTicketDetail(res.data);
            fetchTickets();
        } catch (err) { console.error('Error asignando solicitud:', err); }
    };

    // Filtrado
    const filteredTickets = tickets.filter(t => {
        if (filterStatus && t.status !== filterStatus) return false;
        if (filterCat && t.categoria !== filterCat) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!t.titulo.toLowerCase().includes(q) &&
                !t.creator_name?.toLowerCase().includes(q) &&
                !`#${t.id}`.includes(q)) return false;
        }
        return true;
    });

    const counts = {
        total: tickets.length,
        abierto: tickets.filter(t => t.status === 'abierto').length,
        en_progreso: tickets.filter(t => t.status === 'en_progreso').length,
        cerrado: tickets.filter(t => t.status === 'cerrado').length,
    };

    // ============================================================
    // VISTA: CREAR SOLICITUD
    // ============================================================

    if (view === 'create') {
        return (
            <div className="tk-page">
                <motion.div className="tk-container" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <button className="tk-back" onClick={() => setView('list')}>
                        <ArrowLeft size={20} /> <span>Volver</span>
                    </button>

                    <div className="tk-create-card">
                        <div className="tk-create-header">
                            <h2>Nueva Solicitud</h2>
                            <p>Describe lo que necesitas y el equipo de Servicios Generales se encargara</p>
                        </div>

                        <form onSubmit={handleCreate}>
                            {/* Titulo */}
                            <div className="tk-field">
                                <label>Que necesitas?</label>
                                <input
                                    type="text"
                                    placeholder="Ej: Reparar silla de oficina, mover cajas al piso 3..."
                                    value={form.titulo}
                                    onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                                    maxLength={200}
                                    required
                                    autoFocus
                                />
                            </div>

                            {/* Categoria */}
                            <div className="tk-field">
                                <label>Categoria</label>
                                <div className="tk-cat-grid">
                                    {CATEGORIAS.map(cat => {
                                        const Icon = cat.icon;
                                        const selected = form.categoria === cat.key;
                                        return (
                                            <button
                                                key={cat.key}
                                                type="button"
                                                className={`tk-cat-option ${selected ? 'selected' : ''}`}
                                                style={{ '--cat-color': cat.color }}
                                                onClick={() => setForm(f => ({ ...f, categoria: cat.key }))}
                                            >
                                                <Icon size={18} />
                                                <span>{cat.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Ubicacion */}
                            <div className="tk-field">
                                <label>Ubicacion</label>
                                <div style={{ position: 'relative' }}>
                                    <MapPin size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                                    <select
                                        value={form.ubicacion}
                                        onChange={e => setForm(f => ({ ...f, ubicacion: e.target.value }))}
                                        className="tk-select"
                                        style={{ paddingLeft: 36, width: '100%' }}
                                    >
                                        <option value="">Selecciona la sede</option>
                                        {UBICACIONES.map(u => (
                                            <option key={u.key} value={u.key}>{u.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Descripcion */}
                            <div className="tk-field">
                                <label>Descripcion</label>
                                <textarea
                                    placeholder="Explica con detalle que se necesita hacer..."
                                    value={form.descripcion}
                                    onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                                    rows={5}
                                    required
                                />
                            </div>

                            {/* Adjuntos */}
                            <div className="tk-field">
                                <label>Adjuntos <span className="tk-optional">(opcional)</span></label>
                                <div className="tk-dropzone" onClick={() => fileInputRef.current?.click()}>
                                    <Paperclip size={22} />
                                    <span>Haz click para adjuntar fotos o archivos</span>
                                    <input ref={fileInputRef} type="file" multiple hidden
                                        onChange={e => setFormFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                                </div>
                                {formFiles.length > 0 && (
                                    <div className="tk-chips">
                                        {formFiles.map((f, i) => (
                                            <div key={i} className="tk-chip">
                                                <Paperclip size={13} /> <span>{f.name}</span>
                                                <button type="button" onClick={() => setFormFiles(prev => prev.filter((_, j) => j !== i))}><X size={14} /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button type="submit" className="tk-primary-btn" disabled={creating || !form.titulo || !form.descripcion || !form.categoria}>
                                {creating ? 'Enviando...' : 'Enviar Solicitud'} <Send size={18} />
                            </button>
                        </form>
                    </div>
                </motion.div>
            </div>
        );
    }

    // ============================================================
    // VISTA: DETALLE
    // ============================================================

    if (view === 'detail') {
        const t = ticketDetail;
        return (
            <div className="tk-page">
                <motion.div className="tk-container" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <button className="tk-back" onClick={() => { setView('list'); setTicketDetail(null); }}>
                        <ArrowLeft size={20} /> <span>Volver</span>
                    </button>

                    {loadingDetail ? (
                        <div className="tk-loading">Cargando solicitud...</div>
                    ) : t ? (
                        <div className="tk-detail-card">
                            {/* Header */}
                            <div className="tk-detail-top">
                                <div className="tk-detail-info">
                                    <div className="tk-detail-badges">
                                        <span className="tk-ticket-num">#{t.id}</span>
                                        <StatusBadge status={t.status} />
                                        <CategoryBadge categoria={t.categoria} />
                                        <SlaTimer ticket={t} />
                                    </div>
                                    <h2>{t.titulo}</h2>
                                    <div className="tk-detail-byline">
                                        <span>{t.creator_name}</span>
                                        {t.creator_area && <><span className="tk-dot">&middot;</span><span>{t.creator_area}</span></>}
                                        <span className="tk-dot">&middot;</span>
                                        <span>{formatFullDate(t.created_at)}</span>
                                    </div>
                                    {t.assignee_name && (
                                        <div className="tk-assigned-to">
                                            <UserCheck size={15} /> Asignado a <strong>{t.assignee_name}</strong>
                                        </div>
                                    )}
                                    {t.ubicacion && (
                                        <div className="tk-assigned-to">
                                            <MapPin size={15} /> Ubicacion: <strong>{t.ubicacion}</strong>
                                        </div>
                                    )}
                                    {t.first_response_at && (
                                        <div className="tk-first-response">
                                            <Clock size={14} /> Primera respuesta: {formatFullDate(t.first_response_at)}
                                        </div>
                                    )}
                                </div>

                                {/* Admin actions */}
                                {isAdmin && (
                                    <div className="tk-detail-actions">
                                        {!t.assigned_to && t.status !== 'cerrado' && (
                                            <button className="tk-act-btn teal" onClick={handleAssign}>
                                                <UserCheck size={16} /> Tomar
                                            </button>
                                        )}
                                        {t.status === 'abierto' && (
                                            <button className="tk-act-btn amber" onClick={() => handleStatusChange('en_progreso')} disabled={updatingStatus}>
                                                <CircleDot size={16} /> En progreso
                                            </button>
                                        )}
                                        {t.status !== 'cerrado' && (
                                            <button className="tk-act-btn green" onClick={() => handleStatusChange('cerrado')} disabled={updatingStatus}>
                                                <CircleCheck size={16} /> Cerrar
                                            </button>
                                        )}
                                        {t.status === 'cerrado' && (
                                            <button className="tk-act-btn blue" onClick={() => handleStatusChange('abierto')} disabled={updatingStatus}>
                                                <RotateCcw size={16} /> Reabrir
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Descripcion */}
                            <div className="tk-detail-desc">
                                <p>{t.descripcion}</p>
                                {t.attachments?.length > 0 && (
                                    <div className="tk-detail-files">
                                        {t.attachments.map(a => {
                                            const isImage = a.mime_type?.startsWith('image/');
                                            const url = `${a.download_url}?token=${authToken}`;
                                            return isImage ? (
                                                <a key={a.id} href={url} target="_blank" rel="noreferrer">
                                                    <img src={url} alt={a.original_name} className="tk-inline-img" />
                                                </a>
                                            ) : (
                                                <a key={a.id} href={url} className="tk-file-link" target="_blank" rel="noreferrer">
                                                    <Download size={15} /> {a.original_name}
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Comentarios */}
                            <div className="tk-comments">
                                <h3><MessageSquare size={18} /> Conversacion ({t.comments?.length || 0})</h3>

                                <div className="tk-thread">
                                    {t.comments?.length === 0 && <p className="tk-no-comments">Aun no hay comentarios</p>}
                                    {t.comments?.map((c, i) => (
                                        <motion.div key={c.id} className={`tk-msg ${c.user_role === 'generales' ? 'support' : ''}`}
                                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                                            <div className="tk-msg-head">
                                                <strong>{c.user_name}</strong>
                                                {c.user_role === 'generales' && <span className="tk-support-tag">Servicios Generales</span>}
                                                <span className="tk-msg-time">{formatDate(c.created_at)}</span>
                                            </div>
                                            <p>{c.content}</p>
                                            {c.attachments?.length > 0 && (
                                                <div className="tk-detail-files">
                                                    {c.attachments.map(a => {
                                                        const isImage = a.mime_type?.startsWith('image/');
                                                        const url = `${a.download_url}?token=${authToken}`;
                                                        return isImage ? (
                                                            <a key={a.id} href={url} target="_blank" rel="noreferrer">
                                                                <img src={url} alt={a.original_name} className="tk-inline-img" />
                                                            </a>
                                                        ) : (
                                                            <a key={a.id} href={url} className="tk-file-link" target="_blank" rel="noreferrer">
                                                                <Download size={14} /> {a.original_name}
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                    <div ref={commentsEndRef} />
                                </div>

                                {t.status !== 'cerrado' && (
                                    <div className="tk-reply">
                                        {commentFiles.length > 0 && (
                                            <div className="tk-chips compact">
                                                {commentFiles.map((f, i) => (
                                                    <div key={i} className="tk-chip">
                                                        <Paperclip size={13} /> <span>{f.name}</span>
                                                        <button onClick={() => setCommentFiles(prev => prev.filter((_, j) => j !== i))}><X size={14} /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="tk-reply-bar">
                                            <button className="tk-reply-attach" onClick={() => commentFileRef.current?.click()}>
                                                <Paperclip size={19} />
                                                <input ref={commentFileRef} type="file" multiple hidden
                                                    onChange={e => setCommentFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                                            </button>
                                            <input
                                                type="text"
                                                placeholder="Escribe un comentario..."
                                                value={comment}
                                                onChange={e => setComment(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); } }}
                                            />
                                            <button className="tk-reply-send" onClick={handleComment}
                                                disabled={sendingComment || (!comment.trim() && commentFiles.length === 0)}>
                                                <Send size={18} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}
                </motion.div>
            </div>
        );
    }

    // ============================================================
    // VISTA: LISTA (con tabs admin)
    // ============================================================

    const renderReports = () => {
        if (loadingReports) return <div className="tk-loading">Cargando reportes...</div>;
        if (!reports) return null;
        const r = reports;

        const catColors = {};
        CATEGORIAS.forEach(c => { catColors[c.key] = c.color; });

        const maxTrend = Math.max(...r.tendencia.map(t => Math.max(t.creados, t.cerrados)), 1);

        return (
            <div className="tk-reports">
                {/* KPIs */}
                <div className="tk-kpi-grid">
                    <div className="tk-kpi"><span className="tk-kpi-num">{r.total}</span><span className="tk-kpi-label">Total solicitudes</span></div>
                    <div className="tk-kpi"><span className="tk-kpi-num">{r.avg_resolution_hours}h</span><span className="tk-kpi-label">Tiempo promedio resolucion</span></div>
                    <div className="tk-kpi"><span className="tk-kpi-num">{r.avg_first_response_hours}h</span><span className="tk-kpi-label">Promedio primera respuesta</span></div>
                    <div className="tk-kpi">
                        <span className="tk-kpi-num" style={{ color: r.sla_compliance >= 80 ? '#10b981' : r.sla_compliance >= 50 ? '#f59e0b' : '#ef4444' }}>
                            {r.sla_compliance}%
                        </span>
                        <span className="tk-kpi-label">Cumplimiento SLA</span>
                    </div>
                </div>

                <div className="tk-report-row">
                    {/* Por categoria */}
                    <div className="tk-report-card">
                        <h4>Por categoria</h4>
                        {Object.entries(r.por_categoria).map(([cat, count]) => (
                            <div key={cat} className="tk-bar-row">
                                <span className="tk-bar-label">{getCatInfo(cat).label}</span>
                                <div className="tk-bar-track">
                                    <div className="tk-bar-fill" style={{ width: `${(count / r.total) * 100}%`, backgroundColor: catColors[cat] || '#94a3b8' }} />
                                </div>
                                <span className="tk-bar-val">{count}</span>
                            </div>
                        ))}
                    </div>

                </div>

                {/* Tendencia */}
                <div className="tk-report-card">
                    <h4>Tendencia ultimos 30 dias</h4>
                    <div className="tk-trend-legend">
                        <span><span className="tk-trend-dot" style={{ background: '#3b82f6' }} /> Creados</span>
                        <span><span className="tk-trend-dot" style={{ background: '#10b981' }} /> Cerrados</span>
                    </div>
                    <div className="tk-trend-chart">
                        {r.tendencia.map((d, i) => (
                            <div key={i} className="tk-trend-col" title={`${d.fecha}: ${d.creados} creados, ${d.cerrados} cerrados`}>
                                <div className="tk-trend-bars">
                                    <div className="tk-trend-bar created" style={{ height: `${(d.creados / maxTrend) * 100}%` }} />
                                    <div className="tk-trend-bar closed" style={{ height: `${(d.cerrados / maxTrend) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="tk-page">
            <motion.div className="tk-container" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                {/* Header */}
                <div className="tk-page-header">
                    <h1>Servicios Generales</h1>
                    <button className="tk-primary-btn" onClick={() => setView('create')}>
                        <Plus size={20} /> Nueva Solicitud
                    </button>
                </div>

                {/* Admin Tabs */}
                {isAdmin && (
                    <div className="tk-tabs">
                        <button className={`tk-tab ${adminTab === 'tickets' ? 'active' : ''}`} onClick={() => setAdminTab('tickets')}>
                            <MessageSquare size={16} /> Solicitudes
                        </button>
                        <button className={`tk-tab ${adminTab === 'reports' ? 'active' : ''}`} onClick={() => setAdminTab('reports')}>
                            <BarChart3 size={16} /> Reportes
                        </button>
                    </div>
                )}

                {/* Tab: Tickets */}
                {(adminTab === 'tickets' || !isAdmin) && (
                    <>
                        {/* Stats */}
                        <div className="tk-stats">
                            {Object.entries(ESTADOS).map(([key, val]) => {
                                const Icon = val.icon;
                                const isActive = filterStatus === key;
                                return (
                                    <button key={key} className={`tk-stat ${isActive ? 'active' : ''}`}
                                        onClick={() => setFilterStatus(isActive ? '' : key)}>
                                        <div className="tk-stat-icon" style={{ backgroundColor: val.bg, color: val.color }}>
                                            <Icon size={20} />
                                        </div>
                                        <div className="tk-stat-info">
                                            <span className="tk-stat-num">{counts[key]}</span>
                                            <span className="tk-stat-label">{val.label}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Search + filter */}
                        <div className="tk-toolbar">
                            <div className="tk-searchbox">
                                <Search size={18} />
                                <input type="text" placeholder="Buscar por titulo, #id o nombre..."
                                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                            </div>
                            <select className="tk-select" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
                                <option value="">Todas las categorias</option>
                                {CATEGORIAS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                        </div>

                        {/* List */}
                        {loading ? (
                            <div className="tk-loading">Cargando solicitudes...</div>
                        ) : filteredTickets.length === 0 ? (
                            <div className="tk-empty">
                                <div className="tk-empty-icon"><Search size={40} strokeWidth={1.2} /></div>
                                <h3>No hay solicitudes</h3>
                                <p>{isAdmin ? 'No se encontraron solicitudes con estos filtros' : 'Crea una nueva si necesitas ayuda del equipo de Servicios Generales'}</p>
                            </div>
                        ) : (
                            <div className="tk-list">
                                <AnimatePresence>
                                    {filteredTickets.map((t, i) => {
                                        const cat = getCatInfo(t.categoria);
                                        const CatIcon = cat.icon;
                                        return (
                                            <motion.div key={t.id} className="tk-row" onClick={() => openTicket(t.id)}
                                                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                                                <div className="tk-row-cat" style={{ backgroundColor: cat.color + '14', color: cat.color }}>
                                                    <CatIcon size={22} />
                                                </div>
                                                <div className="tk-row-content">
                                                    <div className="tk-row-top">
                                                        <span className="tk-row-id">#{t.id}</span>
                                                        <StatusBadge status={t.status} />
                                                                    </div>
                                                    <h4>{t.titulo}</h4>
                                                    <div className="tk-row-meta">
                                                        <span>{t.creator_name}</span>
                                                        {t.creator_area && <><span className="tk-dot">&middot;</span><span>{t.creator_area}</span></>}
                                                        <span className="tk-dot">&middot;</span>
                                                        <span>{formatDate(t.created_at)}</span>
                                                        {t.comment_count > 0 && (
                                                            <span className="tk-row-comments"><MessageSquare size={13} /> {t.comment_count}</span>
                                                        )}
                                                        {t.ubicacion && (
                                                            <span className="tk-row-equipo"><MapPin size={13} /> {t.ubicacion}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <ChevronRight size={20} className="tk-row-arrow" />
                                            </motion.div>
                                        );
                                    })}
                                </AnimatePresence>
                            </div>
                        )}
                    </>
                )}

                {/* Tab: Reportes */}
                {adminTab === 'reports' && isAdmin && renderReports()}
            </motion.div>
        </div>
    );
};

export default SolicitudServicio;
